import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../context/MyContext";
import Menu from "../../components/menu/Menu";
import { Mode } from "../tutorV2/types";
import LoginModal from "../chat/modal/LoginModal";
import Loading from "../../components/Loading";
import AirHeader from "../../components/AirHeader";
import Badge from "../../components/badge";
import { Back, Chevron } from "../../assets/svgs/icons";
import { LogoDefault } from "../../assets/svgs/logos";
import useOrderListView, { OrderItem, formatDateDot } from "./hooks/useOrderListView";
import Paging from "../../components/Paging";
import { formatToKoreanWon } from "../../utils/commonFunctions";

// 배지 타입에 따른 색상 및 라벨 매핑
const getBadgeProps = (item: OrderItem) => {
  // TODO: 실제 데이터 구조에 맞게 조건 수정 필요
  // 현재는 is_canceled와 item_name을 기반으로 임시 분류
  if (item.is_canceled) {
    return { color: "gray" as const, label: "취소" };
  }

  // 정기결제 상품인 경우
  if (item.item_name?.includes("정기") || item.item_name?.includes("구독")) {
    return { color: "gray" as const, label: "정기결제" };
  }

  // 이용권 (무료체험권 등)
  if (item.item_name?.includes("무료") || item.item_name?.includes("이용권")) {
    return { color: "main" as const, label: "이용권" };
  }

  // 기본: 구매
  return { color: "gray" as const, label: "구매" };
};

const OrderListV2: React.FC = () => {
  const navigate = useNavigate();
  const { user, isMobile } = useContext(MyContext);
  const isLoggedIn = !!user?.id;

  const {
    orderList,
    loading,
    pageInfo,
    subscriptionPeriod,
    isLogin,
    handleGoBack,
    handleDetailClick,
    setPageInfo,
  } = useOrderListView();

  // 구매하기 버튼 핸들러
  const handlePurchaseClick = () => {
    window.open(
      `${process.env.REACT_APP_DONGA_STORE}/subscription?anchor=2`,
      "_blank"
    );
  };

  // 모바일 레이아웃
  if (isMobile) {
    return (
      <div className="flex flex-col h-full w-full bg-white overflow-hidden">
        {/* Header - 모바일: AirHeader 사용 */}
        <AirHeader type="sub" title="이용 내역" />

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="flex flex-col gap-4 w-full">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loading />
              </div>
            ) : (
              <>
                {/* Period Bar (구독 중) or CTA Card (비구독) */}
                {subscriptionPeriod ? (
                  <div className="w-full rounded-[8px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] flex items-center justify-between px-[16px] py-[20px]">
                    <span className="font-medium text-white text-[14px] leading-[20px] tracking-[-0.28px]">
                      이용기간
                    </span>
                    <span className="font-bold text-white text-[16px] leading-[24px] tracking-[-0.32px]">
                      {subscriptionPeriod.startDate} ~ {subscriptionPeriod.endDate}
                    </span>
                  </div>
                ) : (
                  <div className="w-full rounded-[8px] p-[1px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa]">
                    <div className="w-full rounded-[7px] bg-white flex flex-wrap items-center justify-between gap-3 px-[16px] py-[20px]">
                      <p
                        className="text-[16px] leading-[24px] tracking-[-0.32px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa]"
                        style={{ WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}
                      >
                        <span className="font-bold">과학동아AiR PASS</span>
                        <span className="font-medium"> 구매하고, </span>
                        <span className="font-bold">자유롭게 이용</span>
                        <span className="font-medium">하세요!</span>
                      </p>
                      <button
                        onClick={handlePurchaseClick}
                        className="h-[40px] px-[20px] rounded-[8px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] text-white text-[14px] font-bold leading-[20px] tracking-[-0.28px] shrink-0"
                      >
                        PASS 구매하기
                      </button>
                    </div>
                  </div>
                )}

                {/* Order Table */}
                <div className="w-full border-t border-b border-solid border-[#e2d5fc]">
                  {/* Table Header */}
                  <div className="flex items-center h-[36px] text-[12px] font-bold text-[#212121] tracking-[-0.24px]">
                    <div className="flex-1 text-center">상품정보</div>
                    <div className="w-[80px] text-center">결제금액</div>
                    <div className="w-[40px] text-center">상세</div>
                  </div>

                  {/* Table Body */}
                  {orderList.length > 0 ? (
                    orderList.map((item) => {
                      const badgeProps = getBadgeProps(item);
                      const isCanceled = item.is_canceled;

                      return (
                        <div
                          key={item.order_no}
                          className="flex items-center min-h-[64px] py-3 border-t border-[#eeeeee]"
                        >
                          {/* Product Info */}
                          <div className="flex-1 flex flex-col gap-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge.Round
                                size="sm"
                                type="secondary"
                                style={badgeProps.color === "main" ? "line" : "fill"}
                                color={badgeProps.color}
                              >
                                {badgeProps.label}
                              </Badge.Round>
                              <span className="text-[12px] text-[#888888] tracking-[-0.24px]">
                                {formatDateDot(item.created_at)}
                              </span>
                            </div>
                            <p className="text-[14px] font-medium text-[#212121] tracking-[-0.28px] line-clamp-2">
                              {item.item_name}
                            </p>
                          </div>

                          {/* Price */}
                          <div
                            className={`w-[80px] text-right text-[14px] font-medium tracking-[-0.28px] ${
                              isCanceled ? "text-[#888888] line-through" : "text-[#212121]"
                            }`}
                          >
                            {formatToKoreanWon(item.order_price)}원
                          </div>

                          {/* Detail Arrow */}
                          <div className="w-[40px] flex items-center justify-center">
                            <button
                              onClick={() => handleDetailClick(item)}
                              className="p-2"
                              aria-label="상세 보기"
                            >
                              <Chevron className="w-5 h-5 text-[#888888]" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex items-center justify-center h-[200px] text-[#888888] text-[14px]">
                      구매 내역이 없습니다.
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {pageInfo.total > 0 && (
                  <Paging pageInfo={pageInfo} setPageInfo={setPageInfo} />
                )}
              </>
            )}
          </div>
        </div>

        {/* Login Modal */}
        {isLogin && <LoginModal />}
      </div>
    );
  }

  // PC 레이아웃
  return (
    <div className="flex flex-row h-full w-full bg-white overflow-hidden">
      {/* Sidebar */}
      {isLoggedIn && <Menu mode={Mode.TUTOR} />}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
        {/* Header - PC: Custom header */}
        <header className="relative flex items-center justify-center bg-white w-full h-[64px] shrink-0">
          {/* Back Button */}
          <button
            onClick={handleGoBack}
            className="absolute left-0 top-1/2 -translate-y-1/2 p-4"
            aria-label="뒤로가기"
          >
            <Back className="w-6 h-6 text-[#212121]" />
          </button>

          {/* Logo */}
          <LogoDefault className="w-[100px] h-[25px]" />
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-6 md:py-8">
          <div className="flex flex-col gap-4 md:gap-6 max-w-[800px] mx-auto w-full">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loading />
              </div>
            ) : (
              <>
                {/* Title - PC only */}
                <div className="hidden md:flex flex-col gap-2">
                  <h1 className="font-bold text-[24px] leading-[36px] text-[#212121]">
                    이용 내역
                  </h1>
                </div>

                {/* Period Bar (구독 중) or CTA Card (비구독) */}
                {subscriptionPeriod ? (
                  <div className="w-full py-[20px] rounded-[8px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] flex items-center justify-between px-[16px]">
                    <span className="font-medium text-white text-[14px] leading-[20px] tracking-[-0.28px]">
                      이용기간
                    </span>
                    <span className="font-bold text-white text-[16px] leading-[24px] tracking-[-0.32px]">
                      {subscriptionPeriod.startDate} ~ {subscriptionPeriod.endDate}
                    </span>
                  </div>
                ) : (
                  <div className="w-full rounded-[8px] p-[1px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa]">
                    <div className="w-full rounded-[7px] bg-white flex items-center justify-between px-[16px] py-[20px]">
                      <p
                        className="text-[16px] leading-[24px] tracking-[-0.32px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa]"
                        style={{ WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}
                      >
                        <span className="font-bold">과학동아AiR PASS</span>
                        <span className="font-medium"> 구매하고,</span>
                        <br />
                        <span className="font-bold">자유롭게 이용</span>
                        <span className="font-medium">하세요!</span>
                      </p>
                      <button
                        onClick={handlePurchaseClick}
                        className="h-[40px] px-[20px] rounded-[8px] bg-gradient-to-r from-[#cd3ec7] via-[#5328df] to-[#2d8efa] text-white text-[14px] font-bold leading-[20px] tracking-[-0.28px] shrink-0"
                      >
                        PASS 구매하기
                      </button>
                    </div>
                  </div>
                )}

                {/* Order Table */}
                <div className="w-full flex flex-col flex-1 border-t border-b border-solid border-[#e2d5fc]">
                  {/* Table Header */}
                  <div className="flex items-center h-[36px] text-[12px] font-bold text-[#212121] tracking-[-0.24px]">
                    <div className="flex-1 text-center">상품정보</div>
                    <div className="w-[100px] text-center">결제금액</div>
                    <div className="w-[40px] text-center">상세</div>
                  </div>

                  {/* Table Body */}
                  {orderList.length > 0 ? (
                    orderList.map((item) => {
                      const badgeProps = getBadgeProps(item);
                      const isCanceled = item.is_canceled;

                      return (
                        <div
                          key={item.order_no}
                          className="flex items-center min-h-[64px] border-t border-[#eeeeee]"
                        >
                          {/* Product Info */}
                          <div className="flex-1 flex flex-col gap-[4px] min-w-0 p-[12px]">
                            <div className="flex items-center gap-[4px]">
                              <Badge.Round
                                size="sm"
                                type="secondary"
                                style="line"
                                color={badgeProps.color}
                              >
                                {badgeProps.label}
                              </Badge.Round>
                              <span className="text-[12px] text-[#888888] tracking-[-0.24px]">
                                {formatDateDot(item.created_at)}
                              </span>
                            </div>
                            <p className="text-[14px] font-medium text-[#212121] tracking-[-0.28px] line-clamp-2 md:line-clamp-1">
                              {item.item_name}
                            </p>
                          </div>

                          {/* Price */}
                          <div
                            className={`w-[100px] px-[8px] py-[16px] text-center text-[14px] font-medium tracking-[-0.28px] whitespace-nowrap ${
                              isCanceled ? "text-[#888888] line-through" : "text-[#212121]"
                            }`}
                          >
                            {formatToKoreanWon(item.order_price)}원
                          </div>

                          {/* Detail Arrow */}
                          <div className="w-[40px] py-[16px] flex items-center justify-center">
                            <button
                              onClick={() => handleDetailClick(item)}
                              className="p-[10px]"
                              aria-label="상세 보기"
                            >
                              <Chevron className="w-5 h-5 text-[#888888]" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex-1 flex items-center justify-center bg-[#f7f7f7] min-h-[200px] text-[#888888] text-[12px] tracking-[-0.24px]">
                      구매 내역이 없습니다.
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {pageInfo.total > 0 && (
                  <Paging pageInfo={pageInfo} setPageInfo={setPageInfo} />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {isLogin && <LoginModal />}
    </div>
  );
};

export default OrderListV2;
